/**
 * Comprehensive input validation utilities for different field types
 * Implements industry-standard validation rules
 */

// Field type validators
export const fieldValidators = {
  // Text field validators
  text: {
    maxLength: 200,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length > 200) return { isValid: false, message: "Text cannot exceed 200 characters." };
      return { isValid: true };
    },
    sanitize: (value) => value.trim(),
    allowedChars: /^[a-zA-Z0-9\s\-.,!?'"()]*$/,
  },

  // Name field validators (firstName, lastName, fullName, username)
  name: {
    maxLength: 50,
    minLength: 2,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length < 2) return { isValid: false, message: "Name must be at least 2 characters." };
      if (value.length > 50) return { isValid: false, message: "Name cannot exceed 50 characters." };
      // Only letters, spaces, hyphens, apostrophes
      const nameRegex = /^[a-zA-Z\s\-']*$/;
      if (!nameRegex.test(value)) {
        return { isValid: false, message: "Name can only contain letters, spaces, hyphens, and apostrophes." };
      }
      return { isValid: true };
    },
    sanitize: (value) => value.replace(/[^a-zA-Z\s\-']/g, "").trim(),
    onKeyPress: (e) => {
      const char = String.fromCharCode(e.which);
      if (!/[a-zA-Z\s\-']/.test(char)) {
        e.preventDefault();
      }
    },
  },

  // Phone number field
  phone: {
    maxLength: 15,
    minLength: 7,
    validate: (value) => {
      if (!value) return { isValid: true };
      // Extract only digits
      const digits = value.replace(/\D/g, "");
      if (digits.length < 7) return { isValid: false, message: "Phone must be at least 7 digits." };
      if (digits.length > 15) return { isValid: false, message: "Phone cannot exceed 15 digits." };
      return { isValid: true };
    },
    sanitize: (value) => value.replace(/[^\d+\-().\s]/g, ""),
    onKeyPress: (e) => {
      const char = String.fromCharCode(e.which);
      if (!/[\d+\-().\s]/.test(char)) {
        e.preventDefault();
      }
    },
  },

  // Email field
  email: {
    maxLength: 254,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length > 254) return { isValid: false, message: "Email cannot exceed 254 characters." };
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return { isValid: false, message: "Please enter a valid email address." };
      }
      return { isValid: true };
    },
    sanitize: (value) => value.toLowerCase().trim(),
  },

  // Age field (positive integer, 1-120)
  age: {
    minLength: 1,
    maxLength: 3,
    validate: (value) => {
      if (!value) return { isValid: true };
      const num = parseInt(value, 10);
      if (isNaN(num)) return { isValid: false, message: "Age must be a number." };
      if (num < 1 || num > 120) return { isValid: false, message: "Age must be between 1 and 120." };
      return { isValid: true };
    },
    sanitize: (value) => value.replace(/[^\d]/g, "").substring(0, 3),
    onKeyPress: (e) => {
      const char = String.fromCharCode(e.which);
      if (!/\d/.test(char)) {
        e.preventDefault();
      }
    },
  },

  // Number field (integers only)
  number: {
    validate: (value) => {
      if (!value) return { isValid: true };
      const num = Number(value);
      if (isNaN(num) || !Number.isFinite(num)) {
        return { isValid: false, message: "Please enter a valid number." };
      }
      return { isValid: true };
    },
    sanitize: (value) => value.replace(/[^\d-]/g, ""),
    onKeyPress: (e) => {
      const char = String.fromCharCode(e.which);
      if (!/[\d-]/.test(char)) {
        e.preventDefault();
      }
    },
  },

  // Currency / Price field (positive numbers with up to 2 decimals)
  currency: {
    validate: (value) => {
      if (!value) return { isValid: true };
      const num = parseFloat(value);
      if (isNaN(num) || !Number.isFinite(num)) {
        return { isValid: false, message: "Please enter a valid amount." };
      }
      if (num < 0) return { isValid: false, message: "Amount cannot be negative." };
      if (num > 9999999) return { isValid: false, message: "Amount exceeds maximum limit." };
      // Check decimal places
      const decimalPlaces = (value.split(".")[1] || "").length;
      if (decimalPlaces > 2) {
        return { isValid: false, message: "Amount can have up to 2 decimal places." };
      }
      return { isValid: true };
    },
    sanitize: (value) => value.replace(/[^\d.]/g, ""),
    onKeyPress: (e) => {
      const char = String.fromCharCode(e.which);
      if (!/[\d.]/.test(char)) {
        e.preventDefault();
      }
    },
  },

  // Password field (8-128 chars, must include uppercase, lowercase, number, special char)
  password: {
    minLength: 8,
    maxLength: 128,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length < 8) return { isValid: false, message: "Password must be at least 8 characters." };
      if (value.length > 128) return { isValid: false, message: "Password cannot exceed 128 characters." };
      if (!/[A-Z]/.test(value)) {
        return { isValid: false, message: "Password must contain at least one uppercase letter." };
      }
      if (!/[a-z]/.test(value)) {
        return { isValid: false, message: "Password must contain at least one lowercase letter." };
      }
      if (!/\d/.test(value)) {
        return { isValid: false, message: "Password must contain at least one number." };
      }
      if (!/[\W_]/.test(value)) {
        return { isValid: false, message: "Password must contain at least one special character." };
      }
      return { isValid: true };
    },
    strength: (value) => {
      if (!value) return { level: "none", score: 0 };
      let score = 0;
      if (value.length >= 8) score += 1;
      if (value.length >= 12) score += 1;
      if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
      if (/\d/.test(value)) score += 1;
      if (/[\W_]/.test(value)) score += 1;

      if (score <= 2) return { level: "weak", score };
      if (score <= 3) return { level: "medium", score };
      return { level: "strong", score };
    },
  },

  // URL field (must start with http:// or https://)
  url: {
    validate: (value) => {
      if (!value) return { isValid: true };
      try {
        const url = new URL(value);
        if (!url.protocol.startsWith("http")) {
          return { isValid: false, message: "URL must start with http:// or https://" };
        }
        return { isValid: true };
      } catch {
        return { isValid: false, message: "Please enter a valid URL." };
      }
    },
    sanitize: (value) => value.trim(),
  },

  // Date field (ISO format YYYY-MM-DD)
  date: {
    validate: (value, options = {}) => {
      if (!value) return { isValid: true };
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return { isValid: false, message: "Please enter a valid date." };
      }

      // Check for past dates if it's a future-event field
      if (options.disallowPast) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) {
          return { isValid: false, message: "Date cannot be in the past." };
        }
      }

      // Check for future dates if it's a birthdate field
      if (options.disallowFuture || options.isBirthDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date > today) {
          return { isValid: false, message: "Date cannot be in the future." };
        }
      }

      return { isValid: true };
    },
  },

  // Address field
  address: {
    maxLength: 200,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length > 200) return { isValid: false, message: "Address cannot exceed 200 characters." };
      return { isValid: true };
    },
    sanitize: (value) => value.trim(),
  },

  // Bio / Description field
  bio: {
    maxLength: 500,
    validate: (value) => {
      if (!value) return { isValid: true };
      if (value.length > 500) return { isValid: false, message: "Bio cannot exceed 500 characters." };
      return { isValid: true };
    },
    sanitize: (value) => value.trim(),
  },
};

/**
 * Sanitize input by removing HTML tags and escaping special characters
 */
export const sanitizeInput = (input, fieldType = "text") => {
  if (!input || typeof input !== "string") return "";

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, "");

  // Use field-specific sanitizer if available
  const validator = fieldValidators[fieldType];
  if (validator && validator.sanitize) {
    sanitized = validator.sanitize(sanitized);
  }

  return sanitized;
};

/**
 * Validate input based on field type
 */
export const validateInput = (value, fieldType = "text", options = {}) => {
  const validator = fieldValidators[fieldType];

  if (!validator) {
    return { isValid: true, message: "" };
  }

  return validator.validate(value, options);
};

/**
 * Get password strength
 */
export const getPasswordStrength = (password) => {
  const validator = fieldValidators.password;
  if (validator && validator.strength) {
    return validator.strength(password);
  }
  return { level: "none", score: 0 };
};

/**
 * Validate paste content
 */
export const validatePasteContent = (pastedText, fieldType = "text") => {
  // For numeric fields, ensure pasted content is numeric
  if (fieldType === "number" || fieldType === "age" || fieldType === "currency") {
    const cleanedText = pastedText.replace(/[^\d.-]/g, "");
    const num = parseFloat(cleanedText);
    if (isNaN(num) || !Number.isFinite(num)) {
      return { isValid: false, message: `Cannot paste non-${fieldType} content.` };
    }
  }

  // For email, basic validation
  if (fieldType === "email") {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pastedText.trim())) {
      return { isValid: false, message: "Cannot paste invalid email." };
    }
  }

  // For phone, ensure it contains valid phone chars
  if (fieldType === "phone") {
    if (!/^[\d+\-().\s]*$/.test(pastedText)) {
      return { isValid: false, message: "Cannot paste invalid phone characters." };
    }
  }

  return { isValid: true };
};

export default fieldValidators;
